// InReviewTable.jsx
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../../../context/AuthContext";
import { crmAPI } from "../../../../services/AdminPortal/crmAPI";
import { formatDate } from "../../../../utils/utils";

export default function InReviewTable({
  staff,
  loading,
  selectedAll,
  filteredApplicants,
  selectedApplicants,
  handleSelectAll,
  handleSelectApplicant, // Ensure this is the one used
  handleViewApplication,
  openAssignmentModal,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Parent component now sends paginated data, so we directly use filteredApplicants
  const applicantsToRender = Array.isArray(filteredApplicants)
    ? filteredApplicants
    : [];

  const REQUIRED_DOCUMENTS = {
    salaried: [
      "PAN_CARD",
      "AADHAR_CARD",
      "BANK_STATEMENT",
      "SALARY_SLIP",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
    ],
    "self-employed": [
      "PAN_CARD",
      "AADHAR_CARD",
      "BANK_STATEMENT",
      "INCOME_TAX_RETURN",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
    ],
    "business-owner": [
      "PAN_CARD",
      "AADHAR_CARD",
      "BANK_STATEMENT",
      "GST_DOCUMENT",
      "COMPANY_REGISTRATION_LETTER",
      "MSME_UDYAM_LICENSE",
      "MUNICIPAL_LICENSE",
      "RENTAL_AGREEMENT",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
    ],
    default: [
      "PAN_CARD",
      "AADHAR_CARD",
      "BANK_STATEMENT",
      "SALARY_SLIP",
      "LIVE_SELFIE",
      "CANCELED_CHEQUE",
    ],
  };
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [dropdownPosition, setDropdownPosition] = useState({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest("[data-dropdown-trigger]") &&
        !event.target.closest("[data-dropdown-menu]")
      ) {
        setDropdownOpen({});
      }
    };
    const handleScroll = () => {
      setDropdownOpen({});
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  const toggleDropdown = (applicantId, event) => {
    const isOpening = !dropdownOpen[applicantId];
    setDropdownOpen((prev) => ({ ...prev, [applicantId]: isOpening }));

    if (isOpening && event) {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownPosition((prev) => ({
        ...prev,
        [applicantId]: {
          top: rect.bottom + 8,
          left: rect.left - 48,
        },
      }));
    }
  };
  const handleStaffSelect = (applicant, staffMember) => {
    toggleDropdown(applicant._id); // Close the dropdown immediately
    openAssignmentModal(applicant, staffMember); // Trigger the modal in the parent
  };

  // Handle share application - generate and download PDF
  const handleShareApplication = async (applicationId) => {
    try {
      toast.info("Generating PDF... Please wait.");

      const result = await crmAPI.generateLoanApplicationPDF(applicationId);

      if (result.success) {
        toast.success(`PDF generated successfully! File: ${result.filename}`);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  const getDocumentSummary = (documents = [], employmentType = "salaried") => {
    const requiredDocs =
      REQUIRED_DOCUMENTS[employmentType] || REQUIRED_DOCUMENTS.default;
    
    const verifiedCount = requiredDocs.filter(docType => {
      const doc = documents.find((d) => d.documentType === docType);
      return doc?.status === "verified";
    }).length;
    
    const totalRequired = requiredDocs.length;
    const overallStatus = verifiedCount === totalRequired ? "verified" : "pending";

    return { verifiedCount, totalRequired, overallStatus };
  };

  const DocumentStatusBadge = ({ documents, employmentType }) => {
    const { verifiedCount, totalRequired, overallStatus } =
      getDocumentSummary(documents, employmentType);

    const isVerified = overallStatus === "verified";

    const bgColor = isVerified ? "bg-green-900" : "bg-yellow-900";
    const textColor = isVerified ? "text-green-400" : "text-yellow-400";
    const label = isVerified ? "Verified" : "Pending";

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${bgColor} ${textColor}`}
      >
        {label} • {verifiedCount}/{totalRequired}
      </span>
    );
  };

  return (
    <div className="  bg-[#131313] rounded-lg border border-[#333333] w-full  overflow-x-auto scrollbar-hide">
      <table className="min-w-full">
        <thead className="border-y border-[#333333]">
          <tr>
            <th className="p-4 text-center border-2 border-[#333333]">
              <input
                type="checkbox"
                className="appearance-none h-5 w-5 border-2 border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                  before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                checked={selectedAll}
                onChange={handleSelectAll}
              />
            </th>
            <th className="p-4 text-center border-2 border-[#333333]">DATE</th>
            <th className="p-4 text-center border-2 border-[#333333]">
              APPLICANT NAME
            </th>
            <th className="p-4 text-center border-2 border-[#333333]">
              LOAN AMOUNT
            </th>
            <th className="p-4 text-center border-2 border-[#333333]">
              CREDIT LIMIT
            </th>
            <th className="p-4 text-center border-2 border-[#333333]">
              ASSIGNED TO
            </th>
            <th className="p-4 text-center border-2 border-[#333333]">
              DOCUMENTS
            </th>
            <th className="p-4 text-center border-2 border-[#333333]">
              ACTIONS
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {applicantsToRender.length === 0 ? (
            <tr>
              <td colSpan="8" className="p-6 text-center text-gray-400">
                No rows found
              </td>
            </tr>
          ) : (
            applicantsToRender.map((applicant, index) => (
              <tr
                key={applicant._id || index}
                className="text-white border-t border-[#333333]"
              >
                <td className="p-4 text-center border-2 border-[#333333]">
                  <input
                    type="checkbox"
                    className="appearance-none h-5 w-5 border-2 border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                      before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                    checked={selectedApplicants.includes(applicant._id)}
                    onChange={() => handleSelectApplicant(applicant._id)}
                  />
                </td>
                <td className="p-4 text-center border-2 border-[#333333]">
                  {formatDate(applicant.createdAt)}
                </td>
                <td
                  className={`p-4 text-center border-2 border-[#333333] ${
                    applicant.referredBy !== null ? "flex flex-col gap-1 " : ""
                  }`}
                >
                  {applicant.user?.name || applicant?.username || "N/A"}
                  {applicant.referredBy !== null ? (
                    <span className="text-xs text-gray-400">
                      <span className="text-xs text-[#FFCA20]">
                        Affiliate Application
                      </span>
                    </span>
                  ) : (
                    ""
                  )}
                </td>
                <td className="p-4 text-center border-2 border-[#333333]">
                  <div>
                    <div>{applicant.principle}</div>
                    <div className="text-sm text-gray-400">
                      {applicant.loanType}
                    </div>
                  </div>
                </td>
                <td className="p-4 text-center border-2 border-[#333333]">
                  limit
                </td>
                <td className="p-4 text-center border-2 border-[#333333]">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      {/* Show selected staff name */}
                      <span
                        data-dropdown-trigger
                        className="cursor-pointer"
                        onClick={(e) => toggleDropdown(applicant._id, e)}
                      >
                        {
                          applicant.assignedTo?.name || "Assign to" // Prefer local state, then API data, then default
                        }
                      </span>

                      {/* Dropdown menu */}
                      {dropdownOpen[applicant._id] && (
                        <div
                          data-dropdown-menu
                          className="fixed z-[9999] w-48 rounded-xl shadow-xl border border-[#333333] bg-[#131313]"
                          style={{
                            top: dropdownPosition[applicant._id]?.top || 0,
                            left: dropdownPosition[applicant._id]?.left || 0,
                          }}
                        >
                          {loading ? (
                            <div className="p-3 text-sm text-white">
                              Loading...
                            </div>
                          ) : (
                            staff.map((staffMember, idx) => (
                              <div
                                key={idx}
                                className="p-3 text-sm text-white cursor-pointer hover:bg-[#1f1f1f] transition duration-150"
                                onClick={() =>
                                  handleStaffSelect(applicant, staffMember)
                                }
                              >
                                {staffMember.name}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <ChevronDown
                      data-dropdown-trigger
                      onClick={(e) => toggleDropdown(applicant._id, e)}
                      size={18}
                      className="text-gray-400 cursor-pointer"
                    />
                  </div>
                </td>
                <td className="p-4 text-center border-2 border-[#333333]">
                  <div className="flex items-center justify-center gap-2">
                    <DocumentStatusBadge 
                      documents={applicant.documents} 
                      employmentType={applicant.user?.employmentType}
                    />
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14 9.5C15.0938 9.5 16 10.4062 16 11.5V14C16 15.125 15.0938 16 14 16H2C0.875 16 0 15.125 0 14V11.5C0 10.4062 0.875 9.5 2 9.5H3.65625L5.15625 11H2C1.71875 11 1.5 11.25 1.5 11.5V14C1.5 14.2812 1.71875 14.5 2 14.5H14C14.25 14.5 14.5 14.2812 14.5 14V11.5C14.5 11.25 14.25 11 14 11H10.8125L12.3125 9.5H14ZM13.5 12.75C13.5 13.1875 13.1562 13.5 12.75 13.5C12.3125 13.5 12 13.1875 12 12.75C12 12.3438 12.3125 12 12.75 12C13.1562 12 13.5 12.3438 13.5 12.75ZM7.46875 11.5312L3.21875 7.28125C2.90625 7 2.90625 6.53125 3.21875 6.25C3.5 5.9375 3.96875 5.9375 4.25 6.25L7.25 9.21875V0.75C7.25 0.34375 7.5625 0 8 0C8.40625 0 8.75 0.34375 8.75 0.75V9.21875L11.7188 6.25C12 5.9375 12.4688 5.9375 12.75 6.25C13.0625 6.53125 13.0625 7 12.75 7.28125L8.5 11.5312C8.375 11.6875 8.1875 11.75 8 11.75C7.78125 11.75 7.59375 11.6875 7.46875 11.5312Z"
                        fill="#FFCA20"
                      />
                    </svg>
                  </div>
                </td>

                <td className="p-4 text-center border-2 border-[#333333]">
                  <div className="flex items-center gap-2">
                    <button
                      className="text-[#FFCA20] mr-2 hover:text-[#FFCA20]"
                      onClick={() => {
                        const role = user?.role?.toLowerCase();
                        if (["admin", "subadmin"].includes(role)) {
                          navigate(
                            `/${role}/loan-applications/approval/${applicant._id}`
                          );
                        } else {
                          console.warn("Unknown role:", role);
                        }
                      }}
                    >
                      View
                    </button>
                    <button
                      className="text-[#FFCA20] ml-2 hover:text-[#FFCA20]"
                      onClick={() => handleShareApplication(applicant._id)}
                    >
                      <svg
                        width="16"
                        height="15"
                        viewBox="0 0 16 15"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4.75 5.78125H13.2188L9.25 2.34375C8.9375 2.0625 8.90625 1.59375 9.15625 1.28125C9.4375 0.96875 9.90625 0.9375 10.2188 1.1875L15.7188 5.9375C15.875 6.09375 16 6.3125 16 6.53125C16 6.71875 15.875 6.9375 15.7188 7.09375L10.2188 11.8438C10.0938 11.9688 9.90625 12.0312 9.75 12.0312C9.53125 12.0312 9.3125 11.9375 9.15625 11.75C8.90625 11.4375 8.9375 10.9688 9.25 10.6875L13.2188 7.28125H4.75C2.9375 7.28125 1.5 8.71875 1.5 10.5312V14.2812C1.5 14.6875 1.15625 15 0.75 15C0.3125 15 0 14.6875 0 14.2812V10.5312C0 7.90625 2.125 5.78125 4.75 5.78125Z"
                          fill="#FFCA20"
                        />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
